import { type FC } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import { AccountButton } from '../account-button';
import { NotificationsButton } from '../notifications-button';

export const TOP_NAV_V2_HEIGHT = 52;

// Route-to-breadcrumb mapping
const BREADCRUMB_MAP: Record<string, { section: string; page: string }> = {
  '/dashboard': { section: 'Overview', page: 'Dashboard' },
  '/dashboard/inbox': { section: 'Communication', page: 'Inbox' },
  '/dashboard/customers': { section: 'CRM', page: 'Clients' },
  '/dashboard/tasks': { section: 'Productivity', page: 'Tasks' },
  '/dashboard/calendar': { section: 'Productivity', page: 'Calendar' },
  '/dashboard/sales-pipeline': { section: 'Sales', page: 'Pipeline' },
  '/dashboard/marketing/campaigns': { section: 'Marketing', page: 'Campaigns' },
  '/dashboard/support-hub': { section: 'Support', page: 'Hub' },
  '/dashboard/contractors': { section: 'Company', page: 'Team' },
  '/dashboard/tools': { section: 'Company', page: 'Integrations' },
  '/dashboard/workflows': { section: 'Company', page: 'Workflows' },
  '/dashboard/settings': { section: 'Company', page: 'Settings' },
  '/dashboard/documents': { section: 'Documents', page: 'All' },
};

interface TopNavV2Props {
  onMobileNavOpen?: () => void;
}

export const TopNavV2: FC<TopNavV2Props> = (props) => {
  const { onMobileNavOpen } = props;
  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Get breadcrumb for current route
  const getBreadcrumb = () => {
    // Exact match first
    if (BREADCRUMB_MAP[location.pathname]) {
      return BREADCRUMB_MAP[location.pathname];
    }
    // Prefix match
    const sortedKeys = Object.keys(BREADCRUMB_MAP).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (location.pathname.startsWith(key)) {
        return BREADCRUMB_MAP[key];
      }
    }
    return { section: 'Dashboard', page: '' };
  };

  const breadcrumb = getBreadcrumb();

  return (
    <Box
      component="header"
      sx={{
        borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        position: 'sticky',
        top: 0,
        width: '100%',
        zIndex: 2,
        bgcolor: isDarkMode ? '#161618' : '#F2F8FC',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: { xs: 2, md: 3 }, height: TOP_NAV_V2_HEIGHT }}
      >
        {/* Left side: Hamburger (mobile) + Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {onMobileNavOpen && (
            <IconButton
              onClick={onMobileNavOpen}
              sx={{ display: { xs: 'inline-flex', lg: 'none' }, p: 0.5 }}
            >
              <SvgIcon fontSize="small">
                <Menu01Icon />
              </SvgIcon>
            </IconButton>
          )}
          <Typography
            sx={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: isDarkMode ? '#e8eaed' : '#0f172a',
              letterSpacing: '-0.01em',
            }}
          >
            {breadcrumb.page || breadcrumb.section}
          </Typography>
          {breadcrumb.page && (
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 500,
                color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)',
              }}
            >
              {breadcrumb.section}
            </Typography>
          )}
        </Stack>

        {/* Right side: Notifications + Account */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <NotificationsButton />
          <AccountButton />
        </Stack>
      </Stack>
    </Box>
  );
};

TopNavV2.propTypes = {
  onMobileNavOpen: PropTypes.func,
};
